import type {
  TeamMember,
  TeamPerson,
  TeamRole,
} from "../../../../shared/data/api/teamApi";

/**
 * Rules for the Team assignment panel, kept out of the component so they can be
 * read on their own.
 */

/**
 * `client_view` is not a colleague.
 *
 * It is the client's own read-only login into their books — a different kind of
 * account that happens to live in the same table. Showing it under "who works on
 * these books" invites an admin to give the client an Accountant role, and the
 * role picker would happily let them. Filtered out rather than disabled, because
 * it is not a team member at all.
 */
export const CLIENT_VIEW_ROLE = "client_view";

export const isClientLogin = (person: TeamMember | TeamPerson): boolean =>
  (person.role ?? "").toLowerCase() === CLIENT_VIEW_ROLE;

/** Display name, falling back to the email when nobody set one. */
export const memberLabel = (member: TeamMember | TeamPerson): string =>
  member.userName ?? member.email;

/** Two-letter monogram for the avatar, matching the onboarding wizard. */
export const initials = (member: TeamMember | TeamPerson): string => {
  const source = memberLabel(member);
  const words = source.split(/[\s@._-]+/).filter((w) => w !== "");
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Roles an admin may hand out here.
 *
 * `client_view` is excluded for the reason above. Roles left over from deleted
 * definitions are not a concern — `/api/roles/list` reads the `roles` table,
 * and the orphans (`Testing 3`, `Testint 2`) live only in `role_permissions`.
 */
export const assignableRoles = (roles: TeamRole[]): TeamRole[] =>
  roles.filter(
    (role) =>
      role.name.toLowerCase() !== CLIENT_VIEW_ROLE &&
      // Derived maker-checker roles are written only by the team board's
      // save; handing one out here would bypass the derivation.
      !role.name.startsWith("ZN "),
  );

/**
 * People who can review this member's work.
 *
 * Self is excluded because the backend rejects it with a 400, and an option that
 * always fails is not an option.
 *
 * The client login is excluded for a bigger reason: `/api/managers/assignable`
 * returns every active user, which includes the client's own read-only account.
 * Nothing on the backend stops it being written into `manager_id`, so offering
 * it would let an admin make the client the reviewer of their own accountant's
 * work. Everyone else active is fair game — the tenant does not rank reviewers
 * by seniority, and the endpoint deliberately returns the caller too.
 */
export const reviewerOptions = (
  people: TeamPerson[],
  memberId: number,
): TeamPerson[] =>
  people.filter((person) => person.id !== memberId && !isClientLogin(person));

/** The role a member currently holds, or undefined when `role_id` is orphaned. */
export const roleFor = (
  member: TeamMember,
  roles: TeamRole[],
): TeamRole | undefined => roles.find((role) => role.id === member.roleId);

/**
 * Why this row cannot be saved yet, or null when it can.
 *
 * Mirrors the backend rather than inventing rules: `AuthController::addUser`
 * refuses to create an Accountant or Senior Accountant without a manager, so
 * leaving one of those without a reviewer here would be a state the app declines
 * to create in the first place. `UserController::update` does NOT enforce it on
 * edit, which is exactly why the check has to live in the UI.
 */
export const blockingReason = (
  roleId: number | null,
  managerId: number | null,
  roles: TeamRole[],
): string | null => {
  if (roleId === null) return "Pick a role.";
  const role = roles.find((r) => r.id === roleId);
  if (role === undefined) return "Pick a role.";
  if (role.managerRequired && managerId === null) {
    return `${role.name} needs a reviewer.`;
  }
  return null;
};

/** True when the row differs from what is stored. */
export const isDirty = (
  member: TeamMember,
  roleId: number | null,
  managerId: number | null,
): boolean => roleId !== member.roleId || managerId !== member.managerId;

/**
 * Only the changed fields, so a reviewer edit does not trigger the master sync.
 *
 * `UserController::update` calls `RoleSyncService` whenever `role_id` is present
 * in the payload — not whenever it changed. Sending the unchanged role along
 * with a reviewer edit would fire a pointless POST at the master admin for every
 * reviewer change.
 */
export const changedFields = (
  member: TeamMember,
  roleId: number | null,
  managerId: number | null,
): { role_id?: number; manager_id?: number | null } => {
  const payload: { role_id?: number; manager_id?: number | null } = {};
  if (roleId !== null && roleId !== member.roleId) payload.role_id = roleId;
  if (managerId !== member.managerId) payload.manager_id = managerId;
  return payload;
};

/** Members split into the two groups the panel shows. */
export const splitByActive = (
  members: TeamMember[],
): { active: TeamMember[]; inactive: TeamMember[] } => {
  const team = members.filter((m) => !isClientLogin(m));
  return {
    active: team.filter((m) => m.isActive),
    inactive: team.filter((m) => !m.isActive),
  };
};
